/**
 * useChatConnection - Hook de gerenciamento de conexões WhatsApp.
 */
import { useState, useEffect, useCallback } from 'react';
import { getChatWhatsappConnections } from '../../../services/crmService';
import { uazapiService } from '../../../services/uazapiService';
import { WhatsappConnection } from '../../../types';

export function isOfficialWhatsappConnection(
    connections: WhatsappConnection[],
    selected: WhatsappConnection | undefined,
    connectionId?: string | null,
): boolean {
    const match = connectionId ? connections.find((connection) => connection.id === connectionId) : undefined;
    return Boolean((match ?? selected)?.officialApi);
}
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAppContext } from '../../../contexts/AppContext';

export type WhatsappLiveStatus = 'checking' | 'online' | 'offline' | 'unknown';

export function useChatConnection() {
    const { currentOrganization } = useOrganization();
    const { currentPage } = useAppContext();
    const needsChatData = currentPage === 'chat' || currentPage === 'dashboard';
    const [connections, setConnections] = useState<WhatsappConnection[]>([]);
    const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
    const [isLoadingConnections, setIsLoadingConnections] = useState(false);
    const [whatsappLive, setWhatsappLive] = useState<WhatsappLiveStatus>('unknown');

    const probeWhatsappLive = useCallback(async (conn: WhatsappConnection | undefined) => {
        if (conn?.officialApi) {
            setWhatsappLive(conn.status === 'Conectado' ? 'online' : 'offline');
            return;
        }
        if (!conn?.instanceName || conn.status !== 'Conectado') {
            setWhatsappLive('unknown');
            return;
        }

        setWhatsappLive('checking');
        try {
            const result = await uazapiService.getConnectionState(conn.instanceName);
            const state = result?.instance?.state;
            if (state === 'open') setWhatsappLive('online');
            else if (state === 'connecting') setWhatsappLive('checking');
            else setWhatsappLive('offline');
        } catch {
            // Falha de proxy/rede ≠ sessão caída. Webhook é a fonte de verdade do disconnect.
            setWhatsappLive('unknown');
        }
    }, []);

    useEffect(() => {
        if (!needsChatData || !currentOrganization) {
            if (!needsChatData) setConnections([]);
            return;
        }

        let cancelled = false;

        (async () => {
            setIsLoadingConnections(prev => prev || connections.length === 0);

            try {
                const conns = await getChatWhatsappConnections(currentOrganization.id);
                if (cancelled) return;

                setConnections(conns);

                setSelectedConnectionId(prev => {
                    if (!prev && conns.length > 0) {
                        const active = conns.find(c => c.status === 'Conectado');
                        return (active || conns[0]).id;
                    }
                    if (prev && conns.length > 0 && !conns.find(c => c.id === prev)) {
                        return conns[0].id;
                    }
                    return prev;
                });
            } catch (error) {
                console.error('[Chat] Erro ao carregar conexões:', error);
            } finally {
                if (!cancelled) setIsLoadingConnections(false);
            }
        })();

        return () => { cancelled = true; };
    }, [needsChatData, currentOrganization?.id]);

    const selectedConnection = connections.find(c => c.id === selectedConnectionId);

    useEffect(() => {
        if (!needsChatData) return;
        void probeWhatsappLive(selectedConnection);
    }, [needsChatData, selectedConnection?.id, selectedConnection?.instanceName, selectedConnection?.status, probeWhatsappLive]);

    const refreshConnections = async () => {
        if (!currentOrganization) return;
        try {
            const conns = await getChatWhatsappConnections(currentOrganization.id);
            setConnections(conns);
        } catch (error) {
            console.error('[Chat] Erro ao atualizar conexões:', error);
        }
    };

    return {
        connections,
        selectedConnectionId,
        setSelectedConnectionId,
        selectedConnection,
        isLoadingConnections,
        refreshConnections,
        whatsappLive,
        probeWhatsappLive,
    };
}
